import api from './api';
import { User, LoginCredentials, AuthResponse, PasswordChangeData } from '../types';

class AuthService {
  // Login
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/auth/login', credentials);

      if (response.data.success && response.data.data) {
        const { accessToken, refreshToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
      }

      return response.data;
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Error de conexión' };
    }
  }

  // Logout
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }

  // Verificar autenticación
  async checkAuth(): Promise<User | null> {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return null;

      const response = await api.get<{ success: boolean; data: { user: User } }>('/auth/check');

      if (response.data.success) {
        return response.data.data.user;
      }

      return null;
    } catch (error) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      return null;
    }
  }

  // Refresh token
  async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return false;

      const response = await api.post<AuthResponse>('/auth/refresh', { refreshToken });

      if (response.data.success && response.data.data) {
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        return true;
      }

      return false;
    } catch (error) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      return false;
    }
  }

  // Obtener perfil
  async getProfile(): Promise<User> {
    const response = await api.get<{ success: boolean; data: User }>('/users/profile');
    return response.data.data;
  }

  // Cambiar contraseña
  async changePassword(data: { currentPassword?: string; newPassword: string }): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Error al cambiar contraseña' };
    }
  }

  // Verificar si hay token guardado
  hasToken(): boolean {
    return !!localStorage.getItem('accessToken');
  }

  // Solicitar recuperación de contraseña
  async requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Error al solicitar recuperación' };
    }
  }

  // Verificar token de reset
  async verifyResetToken(token: string): Promise<{ success: boolean; message: string; data?: { email: string; name: string } }> {
    try {
      const response = await api.get(`/auth/reset-password/${token}`);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Token inválido o expirado' };
    }
  }

  // Resetear contraseña con token
  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/reset-password', { token, newPassword });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Error al resetear contraseña' };
    }
  }
}

export default new AuthService();
